// The sample store: used only while the store service (backend/store, :8797) isn't running. It is built from the
// real starter packages in the repo (store/packages, mounted read-only at /store/packages/), with made-up version
// histories so the dashboard has pending, rejected and yanked versions to show. Pages label it "Sample data".
// Changes (yank, approve, reject, fork, offer back, merge) last until the page reloads. Wave 13: the pumpkin carries
// the sample lineage from lineage-sample.ts (versions, forks under other creators, a merged and an open offer back).

import { ApiError } from "./api.ts";
import { findCreator } from "./catalogue.ts";
import type { StoreBackend } from "./store-client.ts";
import { creatorIdOf, filterAssets, type AssetDetail, type AssetSummary, type AssetVersion, type Check, type Manifest, type OpenLicence, type ReviewItem } from "./store.ts";
import { manifestChanges, paramDiff, parseRef, refOf, type DiffView, type ForkSummary, type Lineage, type LineageNode, type Proposal } from "./lineage.ts";
import { DAY, forkScore, PUMPKIN, PUMPKIN_HISTORY, SAMPLE_FORKS, SAMPLE_PROPOSALS, sampleFileChanges, T0, type SampleVersion } from "./lineage-sample.ts";

const PACKAGES = "/store/packages/";
export const FAKE_IDS = [
  "threedom/avatar-human",
  "threedom/mannequin",
  "threedom/avatar-hair",
  "threedom/avatar-tshirt",
  "threedom/avatar-jeans",
  "threedom/avatar-sneakers",
  "threedom/cap",
  "threedom/backpack",
  "threedom/crate",
  "threedom/crate-small",
  // wave 11: the Halloween pack (haunt track); missing packages are skipped until it's merged
  "threedom/halloween-pumpkin",
  "threedom/halloween-gravestone",
  "threedom/halloween-dead-tree",
  "threedom/halloween-fence",
  "threedom/halloween-house-kit",
  "threedom/halloween-candles",
  "threedom/halloween-lantern",
  "threedom/halloween-fog",
  "threedom/halloween-bats",
  "threedom/halloween-ghost",
  "threedom/halloween-pumpkin-head",
  "threedom/halloween-witch-hat",
  "threedom/halloween-skeleton-hoodie",
];


const ALL_PASS: Check[] = [
  { name: "manifest", result: "pass", detail: "asset.json is valid (protocol 0.1)" },
  { name: "hashes", result: "pass", detail: "every file matches its sha256" },
  { name: "code scan", result: "pass", detail: "no fetch, eval, globals or loaders" },
  { name: "render", result: "pass", detail: "renders at default params" },
  { name: "determinism", result: "pass", detail: "two renders are identical" },
  { name: "dispose", result: "pass", detail: "no GPU resources left behind" },
  { name: "budget", result: "pass", detail: "within the declared budget" },
];

interface FakeVersion extends SampleVersion {
  checks?: Check[];
}

const clone = (m: Manifest): Manifest => structuredClone(m);

/** Made-up histories. Versions without an entry get one listed version: the package's own. */
const HISTORY: Record<string, FakeVersion[]> = {
  [PUMPKIN]: PUMPKIN_HISTORY,
  "threedom/crate": [
    {
      version: "1.0.0",
      status: "listed",
      at: T0,
      edit: (m) => {
        const x = clone(m);
        delete x.params?.properties?.wear;
        if (x.params?.properties?.planks) x.params.properties.planks.maximum = 5;
        delete x.physics;
        return x;
      },
    },
    { version: "1.1.0", status: "listed", at: T0 + 6 * DAY },
    {
      version: "1.2.0",
      status: "pending",
      at: T0 + 14 * DAY,
      checks: [...ALL_PASS.slice(0, 2), { name: "code scan", result: "warn", detail: "declares the audio capability: plays a creak when hit" }, ...ALL_PASS.slice(3)],
      edit: (m) => {
        const x = clone(m);
        x.params!.properties!.lid = { type: "boolean", default: false, description: "add a hinged lid" };
        x.capabilities = ["audio"];
        x.files = { ...x.files, "dist/three.js": "sha256-Q2hhbmdlZEFkYXB0ZXJDb2RlRm9yVGhlTGlkMTIzNA=", "data/creak.ogg": "sha256-Y3JlYWsub2dnIHNhbXBsZSBoYXNoIGZvciByZXZpZXc=" };
        x.budget = { ...x.budget, triangles: 2600 };
        return x;
      },
    },
  ],
  "threedom/cap": [
    { version: "0.9.0", status: "yanked", at: T0 + 2 * DAY, yankReason: "The brim clipped through long hair. Fixed in 1.0.0." },
    { version: "1.0.0", status: "listed", at: T0 + 5 * DAY },
  ],
  "threedom/backpack": [
    { version: "1.0.0", status: "listed", at: T0 + 3 * DAY },
    {
      version: "1.1.0",
      status: "rejected",
      at: T0 + 12 * DAY,
      note: "The straps float about 2 cm off the shoulders at build 1.3. Fit them to the chest bone and send it again.",
      checks: [...ALL_PASS.slice(0, 3), { name: "render", result: "warn", detail: "straps don't touch the body at build 1.3" }, ...ALL_PASS.slice(4)],
      edit: (m) => {
        const x = clone(m);
        x.title = "Small Backpack (roll-top)";
        x.price = 200;
        return x;
      },
    },
  ],
  "threedom/mannequin": [
    { version: "1.0.0", status: "listed", at: T0 },
    {
      version: "1.0.1",
      status: "pending",
      at: T0 + 15 * DAY,
      edit: (m) => {
        const x = clone(m);
        x.tags = [...(x.tags ?? []), "rig"];
        if (x.params?.properties?.height) x.params.properties.height.maximum = 2.2;
        return x;
      },
    },
  ],
};

interface State {
  manifests: Map<string, Manifest>;
  versions: Map<string, FakeVersion[]>;
  bytes: Map<string, number>;
  /** where a package's files are on the mount: a sample fork shows its parent's files */
  dirs: Map<string, string>;
  /** sample forks: id → the ref they were forked from */
  parents: Map<string, string>;
  proposals: Proposal[];
  /** forks made on this page: drafts, so not public */
  drafts: Set<string>;
}

const dirOf = (s: State, id: string): string => s.dirs.get(id) ?? id;

let state: Promise<State> | null = null;

function load(): Promise<State> {
  state ??= (async () => {
    const manifests = new Map<string, Manifest>();
    await Promise.all(
      FAKE_IDS.map(async (id) => {
        const r = await fetch(`${PACKAGES}${id}/asset.json`);
        if (r.ok) manifests.set(id, (await r.json()) as Manifest);
      }),
    );
    const versions = new Map<string, FakeVersion[]>();
    for (const [id, m] of manifests) {
      const h = HISTORY[id]?.map((v) => ({ ...v })) ?? [{ version: m.version, status: "listed" as const, at: T0 + (FAKE_IDS.indexOf(id) % 7) * DAY }];
      versions.set(id, h);
    }
    const s: State = { manifests, versions, bytes: new Map(), dirs: new Map(), parents: new Map(), proposals: SAMPLE_PROPOSALS.map((p) => ({ ...p })), drafts: new Set() };
    // the sample forks: each one's base is its parent at the forked version, under the fork's own name and creator
    for (const f of SAMPLE_FORKS) {
      const pr = parseRef(f.parent);
      const pv = pr && versions.get(pr.id)?.find((v) => v.version === pr.version);
      if (!pr || !pv) continue;
      const base: Manifest = { ...manifestAt(s, pr.id, pv), id: f.id, title: f.title, author: { handle: f.id.split("/")[0]!, verified: false }, parent: f.parent };
      delete base.credits;
      manifests.set(f.id, base);
      versions.set(f.id, f.versions.map((v) => ({ ...v })));
      s.dirs.set(f.id, dirOf(s, pr.id));
      s.parents.set(f.id, f.parent);
    }
    return s;
  })();
  return state;
}

function manifestAt(s: State, id: string, v: FakeVersion): Manifest {
  const base = s.manifests.get(id)!;
  const m = v.edit ? v.edit(base) : clone(base);
  // the merge credits so far travel in the signed manifest
  const vs = s.versions.get(id) ?? [];
  const credits = vs.slice(0, vs.indexOf(v) + 1).flatMap((x) => (x.mergedFrom ? [x.mergedFrom] : []));
  return { ...m, version: v.version, ...(credits.length ? { credits } : {}) };
}

/** The sample's open licences (owner decision 3): free assets forkable with the defaults, paid ones closed. */
function openOf(m: Manifest): OpenLicence {
  if ((m.price ?? 0) > 0) return { models: null, code: null, forkable: false };
  return { models: "CC-BY-4.0", code: m.engines && Object.keys(m.engines).length ? "MIT" : null, forkable: true };
}

function latestListed(vs: FakeVersion[]): FakeVersion | undefined {
  return [...vs].reverse().find((v) => v.status === "listed");
}

/** Package size from the files' Content-Length (HEAD on the mount). */
async function sizeOf(s: State, id: string): Promise<number | undefined> {
  if (s.bytes.has(id)) return s.bytes.get(id);
  const m = s.manifests.get(id);
  if (!m?.files) return undefined;
  const sizes = await Promise.all(
    [...Object.keys(m.files), "asset.json"].map(async (f) => {
      const r = await fetch(`${PACKAGES}${dirOf(s, id)}/${f}`, { method: "HEAD" }).catch(() => null);
      return Number(r?.headers.get("content-length") ?? 0);
    }),
  );
  const total = sizes.reduce((a, b) => a + b, 0);
  s.bytes.set(id, total);
  return total;
}

function summary(s: State, id: string, v: FakeVersion): AssetSummary {
  const m = manifestAt(s, id, v);
  return {
    id,
    title: m.title,
    kind: m.kind,
    tags: m.tags ?? [],
    conventions: m.conventions ?? [],
    creator: { handle: m.author.handle, name: m.author.handle === "threedom" ? "Threedom" : m.author.handle, verified: m.author.verified === true, creatorId: creatorIdOf(m.author.handle) },
    price: m.price ?? 0,
    licence: m.licence.tier,
    cosmetic: m.cosmetic === true,
    version: v.version,
    previewUrl: `${PACKAGES}${dirOf(s, id)}/preview.png`,
    updatedAt: v.at,
    parent: s.parents.get(id) ?? null,
    openLicence: openOf(m),
  };
}

async function detail(s: State, id: string, all: boolean): Promise<AssetDetail | null> {
  const vs = s.versions.get(id);
  if (!vs) return null;
  const top = latestListed(vs) ?? (all ? vs[vs.length - 1] : undefined);
  if (!top) return null;
  const bytes = await sizeOf(s, id);
  const versions: AssetVersion[] = vs
    .filter((v) => all || v.status === "listed" || v.status === "yanked")
    .map((v) => ({
      version: v.version,
      status: v.status,
      publishedAt: v.at,
      note: v.note ?? null,
      yankReason: v.yankReason ?? null,
      checks: v.checks ?? ALL_PASS,
      signed: v.status === "listed",
      stats: bytes ? { bytes } : undefined,
      base: `${PACKAGES}${dirOf(s, id)}/`,
      manifest: manifestAt(s, id, v),
      previewUrl: v.status === "listed" || v.status === "yanked" ? `${PACKAGES}${dirOf(s, id)}/preview.png` : null,
      parent: s.parents.get(id) ?? null,
      mergedFrom: v.mergedFrom ?? null,
      credits: (manifestAt(s, id, v).credits as string[] | undefined) ?? [],
      openLicence: openOf(manifestAt(s, id, v)),
    }))
    .reverse();
  const sum = summary(s, id, top);
  return {
    ...sum,
    stats: bytes ? { bytes } : undefined,
    manifest: manifestAt(s, id, top),
    base: `${PACKAGES}${dirOf(s, id)}/`,
    versions,
    downloads: 40 + ((id.length * 37) % 400),
    forks: forksOf(s, id).length,
    openProposals: s.proposals.filter((p) => p.package === id && p.status === "open").length,
  };
}

const isPublic = (v: FakeVersion) => v.status === "listed" || v.status === "yanked";

/** Listed sample forks straight from `id`. */
function forksOf(s: State, id: string): string[] {
  return [...s.parents].filter(([fid, ref]) => parseRef(ref)?.id === id && s.versions.get(fid)?.some((v) => v.status === "listed")).map(([fid]) => fid);
}

function versionAt(s: State, ref: string): { id: string; v: FakeVersion } {
  const r = parseRef(ref);
  const v = r && s.versions.get(r.id)?.find((x) => x.version === r.version && isPublic(x));
  if (!r || !v) throw new ApiError(404, "unknown_version", `No version ${ref}.`);
  return { id: r.id, v };
}

function nodeOf(s: State, id: string, depth: number): LineageNode {
  const top = latestListed(s.versions.get(id) ?? []);
  return {
    id,
    title: s.manifests.get(id)?.title ?? null,
    handle: id.split("/")[0]!,
    parent: s.parents.get(id) ?? null,
    version: top?.version ?? null,
    forks: depth < 3 ? forksOf(s, id).map((k) => nodeOf(s, k, depth + 1)) : [],
  };
}

export const fakeStore: StoreBackend = {
  live: false,
  async search(f) {
    const s = await load();
    const list: AssetSummary[] = [];
    for (const [id, vs] of s.versions) {
      const top = latestListed(vs);
      if (top) list.push(summary(s, id, top));
    }
    return filterAssets(list, f);
  },
  async asset(id) {
    return detail(await load(), id, false);
  },
  async creator(handle) {
    const s = await load();
    const assets = await fakeStore.search({ sort: "title" });
    const mine = assets.filter((a) => a.creator.handle === handle);
    if (!mine.length && ![...s.manifests.values()].some((m) => m.author.handle === handle)) {
      // same as the live store: a handle-only creator (accounts) has a page with no assets
      const found = await findCreator(creatorIdOf(handle)).catch(() => null);
      return found ? { handle, name: found.creator.name, creatorId: found.creator.id, assets: [] } : null;
    }
    return { handle, name: handle === "threedom" ? "Threedom" : handle, creatorId: creatorIdOf(handle), assets: mine };
  },
  async mine() {
    // sample data: every signed-in user sees the starter creator's assets as their own
    const s = await load();
    const out: AssetDetail[] = [];
    for (const id of s.versions.keys()) {
      if (s.parents.has(id)) continue; // other creators' sample forks

      const d = await detail(s, id, true);
      if (d) out.push(d);
    }
    return out;
  },
  async yank(id, version, reason) {
    const s = await load();
    const v = s.versions.get(id)?.find((x) => x.version === version);
    if (!v) throw new ApiError(404, "version_not_found", "No such version.");
    if (v.status !== "listed") throw new ApiError(409, "not_listed", "Only listed versions can be yanked.");
    v.status = "yanked";
    v.yankReason = reason || undefined;
  },
  async queue() {
    const s = await load();
    const out: ReviewItem[] = [];
    for (const [id, vs] of s.versions) {
      vs.forEach((v, i) => {
        if (v.status !== "pending") return;
        const m = manifestAt(s, id, v);
        const prev = [...vs.slice(0, i)].reverse().find((p) => p.status === "listed" || p.status === "yanked");
        out.push({
          id,
          version: v.version,
          title: m.title,
          kind: m.kind,
          creator: { handle: m.author.handle, name: m.author.handle === "threedom" ? "Threedom" : undefined },
          submittedAt: v.at,
          checks: v.checks ?? ALL_PASS,
          manifest: m,
          previous: prev ? { version: prev.version, manifest: manifestAt(s, id, prev) } : null,
          base: `${PACKAGES}${id}/`,
          previewUrl: `${PACKAGES}${id}/preview.png`,
          hasCode: !!m.engines && Object.keys(m.engines).length > 0,
          renderMissing: false,
        });
      });
    }
    return out.sort((a, b) => a.submittedAt - b.submittedAt);
  },
  async review(id, version, decision, note) {
    const s = await load();
    const v = s.versions.get(id)?.find((x) => x.version === version);
    if (!v || v.status !== "pending") throw new ApiError(409, "not_pending", "That version isn't waiting for review any more.");
    if (decision === "reject" && !note.trim()) throw new ApiError(400, "note_required", "Say what needs fixing.");
    v.status = decision === "approve" ? "listed" : "rejected";
    v.note = note.trim() || undefined;
  },
  openFiles() {
    return true;
  },
  async fileUrl(id, _version, path) {
    return `${PACKAGES}${id}/${path}`;
  },
  async licensed() {
    return null; // the page asks money's inventory
  },
  async runtime() {
    return null; // the install box then shows the runtime's URL pattern
  },
  async renderQueue() {
    return { counts: { queued: 0, running: 0, failed: 0, done: 0 }, jobs: new Map() };
  },
  async retryRender() {
    throw new ApiError(409, "sample_store", "The sample store has no render reviewer.");
  },
  async forks(id, sort = "best") {
    const s = await load();
    const now = Math.floor(Date.now() / 1000);
    const list: ForkSummary[] = forksOf(s, id).map((fid) => {
      const vs = s.versions.get(fid)!;
      const sum = summary(s, fid, latestListed(vs)!);
      const counts = { versions: vs.filter((v) => v.status === "listed").length, forks: forksOf(s, fid).length, merged: s.proposals.filter((p) => p.fromPackage === fid && p.status === "merged").length };
      return { ...sum, ...counts, score: forkScore({ ...counts, updatedAt: sum.updatedAt ?? 0 }, now) };
    });
    list.sort((a, b) => (sort === "recent" ? 0 : b.score - a.score) || (b.updatedAt ?? 0) - (a.updatedAt ?? 0) || a.id.localeCompare(b.id));
    return { count: list.length, forks: list };
  },
  async lineage(id) {
    const s = await load();
    if (!s.manifests.has(id)) throw new ApiError(404, "unknown_package", `No package ${id}.`);
    const ancestors: Lineage["ancestors"] = [];
    for (let cur = s.parents.get(id); cur && ancestors.length < 20; ) {
      const r = parseRef(cur)!;
      ancestors.unshift({ ref: cur, title: s.manifests.get(r.id)?.title ?? null, handle: r.handle });
      cur = s.parents.get(r.id);
    }
    const tree = forksOf(s, id).map((k) => nodeOf(s, k, 2));
    const count = (ns: LineageNode[]): number => ns.reduce((n, x) => n + 1 + count(x.forks), 0);
    return { id, parent: s.parents.get(id) ?? null, root: ancestors[0]?.ref ?? null, ancestors, forks: tree.length, descendants: count(tree), tree };
  },
  async diff(id, from, to) {
    const s = await load();
    const vs = (s.versions.get(id) ?? []).filter(isPublic);
    const top = latestListed(vs);
    const toRef = to ?? (top ? refOf(id, top.version) : null);
    if (!toRef) throw new ApiError(404, "unknown_version", `${id} has no listed version.`);
    const b = versionAt(s, toRef);
    let fromRef = from ?? null;
    if (!fromRef) {
      // the public version before it, else (a fork's first version) its parent
      const older = b.id === id ? vs.slice(0, vs.indexOf(b.v)).pop() : undefined;
      fromRef = older ? refOf(id, older.version) : (s.parents.get(id) ?? null);
    }
    if (!fromRef) return null;
    const a = versionAt(s, fromRef);
    const ma = manifestAt(s, a.id, a.v);
    const mb = manifestAt(s, b.id, b.v);
    const params = paramDiff(ma, mb);
    const manifest = manifestChanges(ma, mb);
    const view: DiffView = {
      from: fromRef,
      to: toRef,
      params,
      manifest,
      files: sampleFileChanges(params, params.length > 0 || manifest.length > 0),
      previews: { from: `${PACKAGES}${dirOf(s, a.id)}/preview.png`, to: `${PACKAGES}${dirOf(s, b.id)}/preview.png` },
    };
    return view;
  },
  async proposals(id, opts = {}) {
    const s = await load();
    return s.proposals
      .filter((p) => (opts.from ? p.fromPackage === id : p.package === id) && (!opts.status || p.status === opts.status))
      .sort((a, b) => b.createdAt - a.createdAt)
      .map((p) => ({ ...p }));
  },
  async propose(parentId, fromRef, title, message) {
    const s = await load();
    const r = parseRef(fromRef);
    if (!r || !title.trim()) throw new ApiError(400, "bad_request", "Give it a title.");
    versionAt(s, fromRef);
    let up = s.parents.get(r.id);
    while (up && parseRef(up)?.id !== parentId) up = s.parents.get(parseRef(up)!.id);
    if (!up) throw new ApiError(400, "not_a_fork", `${r.id} isn't a fork of ${parentId}.`);
    if (s.proposals.some((p) => p.package === parentId && p.from === fromRef && p.status === "open")) throw new ApiError(409, "already_proposed", "Already offered.");
    const p: Proposal = { id: `pr_sample_${s.proposals.length + 1}`, package: parentId, from: fromRef, fromPackage: r.id, fromHandle: r.handle, title: title.trim(), message: message.trim() || null, status: "open", mergedVersion: null, mergedRef: null, note: null, createdAt: Math.floor(Date.now() / 1000), decidedAt: null };
    s.proposals.push(p);
    return { ...p };
  },
  async decideProposal(id, prId, action, opts = {}) {
    const s = await load();
    const p = s.proposals.find((x) => x.id === prId && x.package === id);
    if (!p) throw new ApiError(404, "unknown_proposal", "No such offer.");
    if (p.status !== "open") throw new ApiError(409, "not_open", `That offer is ${p.status}.`);
    const now = Math.floor(Date.now() / 1000);
    if (action === "merge") {
      const vs = s.versions.get(id)!;
      const v = opts.version ?? "";
      const latest = latestListed(vs)?.version ?? "0.0.0";
      if (!/^\d+\.\d+\.\d+$/.test(v) || semverLess(v, latest) || v === latest) throw new ApiError(400, "bad_version", "Pick a newer version.");
      if (vs.some((x) => x.version === v)) throw new ApiError(409, "version_exists", "That version exists.");
      const fork = versionAt(s, p.from);
      const fm = manifestAt(s, fork.id, fork.v);
      const own = s.manifests.get(id)!;
      vs.push({ version: v, status: "pending", at: now, mergedFrom: p.from, edit: () => ({ ...fm, id, title: own.title, author: own.author, price: own.price, licence: own.licence, parent: undefined }) });
      Object.assign(p, { status: "merged", mergedVersion: v, mergedRef: refOf(id, v) });
    } else {
      p.status = action === "decline" ? "declined" : "withdrawn";
    }
    Object.assign(p, { note: opts.note?.trim() || null, decidedAt: now });
    return { ...p };
  },
  async fork(id, as, version) {
    const s = await load();
    const vs = s.versions.get(id) ?? [];
    const v = version ? vs.find((x) => x.version === version && x.status === "listed") : latestListed(vs);
    if (!v) throw new ApiError(404, "unknown_package", `No listed package ${id}.`);
    if (!openOf(manifestAt(s, id, v)).forkable) throw new ApiError(403, "not_forkable", "Not open for forks.");
    if (s.manifests.has(as) || s.drafts.has(as)) throw new ApiError(409, "package_exists", `${as} already exists.`);
    s.drafts.add(as);
    return { id: as, version: v.version, status: "uploading", parent: refOf(id, v.version) };
  },
};

function semverLess(a: string, b: string): boolean {
  const pa = a.split(".").map(Number);
  const pb = b.split(".").map(Number);
  for (let i = 0; i < 3; i++) if (pa[i] !== pb[i]) return pa[i]! < pb[i]!;
  return false;
}
